import React, { useContext, useEffect, useRef, useState } from 'react';
import { Link, useHistory } from 'react-router-dom';
import { getAuth, signInWithEmailAndPassword, signInWithPopup, GoogleAuthProvider } from 'firebase/auth';
import { FcGoogle } from 'react-icons/fc';
import posthog from 'posthog-js';
import { EyeIcon, EyeSlashIcon } from '@heroicons/react/20/solid';

import app from '../utils/firebaseApp.js';
import { AuthContext } from '../utils/auth.js';
import { isIPBlocked, getClientIP, storeRegistrationInfo } from '../utils/ipBlocker.js';
import { consumeIntendedPath, authErrorMessage } from '../utils/authRedirect.js';
import AuthLayout, { AuthDivider } from '../components/layout/AuthLayout.js';
import { Button, Field, Input, Callout } from '../ui/index.js';
import useDocumentTitle from '../hooks/useDocumentTitle.js';

function Login() {
  useDocumentTitle('Sign in');
  const history = useHistory();
  const { currentUser } = useContext(AuthContext);

  const [isLoading, setIsLoading] = useState(false);
  const [googleLoading, setGoogleLoading] = useState(false);
  const [error, setError] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [clientIP, setClientIP] = useState(null);
  const submittingRef = useRef(false);

  useEffect(() => {
    getClientIP().then(setClientIP);
  }, []);

  // Already signed in and not in the middle of a submission: go to the app.
  useEffect(() => {
    if (currentUser && !submittingRef.current) {
      history.replace(consumeIntendedPath());
    }
  }, [currentUser, history]);

  const blocked = () => {
    if (clientIP && isIPBlocked(clientIP)) {
      setError('Sign in is not available from your network.');
      return true;
    }
    return false;
  };

  const finishSignIn = async (user, type) => {
    try {
      await storeRegistrationInfo({ type: 'login', registrationType: type, idToken: await user.getIdToken(), clientIP });
    } catch (e) {
      // non blocking
    }
    try {
      posthog.identify(user.uid, { email: user.email, userId: user.uid, clientIp: clientIP });
      posthog.capture('login', { type, email: user.email, clientIp: clientIP });
      window.dataLayer?.push({ event: 'login', type });
    } catch (e) {
      // analytics is best effort
    }
    history.replace(consumeIntendedPath());
  };

  const signInWithGoogle = async () => {
    if (blocked()) return;
    setError('');
    setGoogleLoading(true);
    submittingRef.current = true;
    try {
      const result = await signInWithPopup(getAuth(app), new GoogleAuthProvider());
      await finishSignIn(result.user, 'google');
    } catch (err) {
      setError(authErrorMessage(err, 'Google sign-in failed. Please try again.'));
      submittingRef.current = false;
      setGoogleLoading(false);
    }
  };

  const handleLogin = async (event) => {
    event.preventDefault();
    if (blocked()) return;
    const { email, password } = event.target.elements;
    setError('');
    setIsLoading(true);
    submittingRef.current = true;
    try {
      const result = await signInWithEmailAndPassword(getAuth(app), email.value.trim(), password.value);
      await finishSignIn(result.user, 'email');
    } catch (err) {
      setError(authErrorMessage(err, 'Invalid email or password.'));
      submittingRef.current = false;
      setIsLoading(false);
    }
  };

  return (
    <AuthLayout
      title="Welcome back"
      description="Sign in to your Extracta LABS account."
      footer={
        <>
          New to Extracta?{' '}
          <Link to="/register" className="font-semibold text-indigo-600 hover:text-indigo-500">
            Create an account
          </Link>
        </>
      }
    >
      {error && (
        <Callout tone="danger" compact className="mb-5" onDismiss={() => setError('')}>
          {error}
        </Callout>
      )}

      <Button type="button" variant="secondary" size="lg" block onClick={signInWithGoogle} loading={googleLoading} disabled={isLoading}>
        {!googleLoading && <FcGoogle size={18} aria-hidden="true" />}
        Continue with Google
      </Button>

      <AuthDivider>or sign in with email</AuthDivider>

      <form className="space-y-5" onSubmit={handleLogin} noValidate>
        <Field label="Email address" htmlFor="email">
          <Input id="email" name="email" type="email" autoComplete="email" required placeholder="you@company.com" size="lg" />
        </Field>

        <Field
          label="Password"
          htmlFor="password"
          trailing={
            <Link to="/forgot" className="font-medium text-indigo-600 hover:text-indigo-500">
              Forgot password?
            </Link>
          }
        >
          <Input
            id="password"
            name="password"
            type={showPassword ? 'text' : 'password'}
            autoComplete="current-password"
            required
            placeholder="••••••••"
            size="lg"
            trailing={
              <button type="button" onClick={() => setShowPassword((v) => !v)} className="rounded-md p-1 text-gray-400 hover:text-gray-600" aria-label={showPassword ? 'Hide password' : 'Show password'}>
                {showPassword ? <EyeSlashIcon className="h-4 w-4" /> : <EyeIcon className="h-4 w-4" />}
              </button>
            }
          />
        </Field>

        <Button type="submit" size="lg" block loading={isLoading} disabled={googleLoading}>
          Sign in
        </Button>
      </form>
    </AuthLayout>
  );
}

export default Login;
